import { createContext, type ReactNode } from "react";
import { defaultError } from "@-/com.npmjs.react-context-default-error";

type HostListener = (node: ReactNode) => void;

export type PrimaryControlsStripTitleRegistry = {
  getCurrent: () => ReactNode;
  publish: (node: ReactNode) => () => void;
  publishOptimistic: (node: ReactNode) => () => void;
  subscribe: (listener: HostListener) => () => void;
};

export function createPrimaryControlsStripTitleRegistry(): PrimaryControlsStripTitleRegistry {
  const entries = new Map<number, ReactNode>();
  let nextId = 0;
  const listeners = new Set<HostListener>();
  let optimisticEntry: { id: number; node: ReactNode } | null = null;

  const getCurrent = (): ReactNode => {
    if (optimisticEntry !== null) {
      return optimisticEntry.node;
    }
    if (entries.size === 0) {
      return null;
    }
    let last: ReactNode = null;
    for (const node of entries.values()) {
      last = node;
    }
    return last;
  };

  const notify = () => {
    const current = getCurrent();
    for (const listener of listeners) {
      listener(current);
    }
  };

  return {
    getCurrent,
    publish(node) {
      const id = ++nextId;
      entries.set(id, node);
      // A focused screen is now authoritative. Clear the tab-press override in the same
      // notification so the host never paints an intermediate fallback title.
      optimisticEntry = null;
      notify();
      return () => {
        if (entries.delete(id)) {
          notify();
        }
      };
    },
    publishOptimistic(node) {
      const id = ++nextId;
      optimisticEntry = { id, node };
      notify();
      return () => {
        if (optimisticEntry?.id === id) {
          optimisticEntry = null;
          notify();
        }
      };
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const PrimaryControlsStripTitleContext = createContext(
  defaultError<PrimaryControlsStripTitleRegistry>("PrimaryControlsStripTitleContext"),
);
